import styles from "./ViewCampaign.module.css";
import { getPageFromName } from "../../route_utils";
import { useEffect, useState, useRef, useContext, useMemo, useCallback } from "react";
import { viewCampaignChartOption } from "../../EchartsUtils/ViewCampaignChartOptionBar";
import { useNavigate, useLocation } from "react-router-dom";
import api_endpoints from "../../api";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";
import AuthContext from "../../context/AuthContext/AuthContext";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import { BASE_URL } from "../../api/constants";
import QRCode from "qrcode";
import PredefinedPeriods from "../../widgets/CustomDatePicker/PredefinedPeriod";
import { dateTimeReadable, formatDateTime } from "../../utils";
import { useApiClientQuery, useApiClientMutation } from "../../hooks/useApiClient";
import dayjs from "dayjs";
import FiltersBar from "./components/FiltersBar";
import AddCashDialog from "./components/AddCashDialog";
import KioskInfoDialog from "./components/KioskInfoDialog";
import ChartAndTotals from "./components/ChartAndTotals";
import CampaignDetails from "./components/CampaignDetails";
import StatsPanel from "./components/StatsPanel";
import ButtonsBar from "./components/ButtonsBar";
import KiosksTable from "./components/KiosksTable";
import CashDonationsTable from "./components/CashDonationsTable";
import CampaignQrPrintDialog from "./components/CampaignQrPrintDialog";
import { Box } from "@mui/material";

/** -------------------------
 *  Currency / Number helpers
 *  ------------------------- */
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "0.00";
  const n = Number(value);
  if (!isFinite(n)) return "0.00";
  return n.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const toNumber = (v) => (isFinite(Number(v)) ? Number(v) : 0);
const dateReadable = (value, locale = "fr-CA") => {
  if (!value) return "-";

  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    year: "numeric",
    month: "long",
    day: "2-digit",
  }).format(new Date(value));
};

/** Palette for series (used when composing the chart option) */
const SERIES_PALETTE = {
  card: "#3B82F6",
  cash: "#10B981",
  check: "#A855F7",
  bank: "#06B6D4",
};

const PRINT_COPY = {
  en: {
    printButton: "Print or Save to PDF",
    qrAlt: "Campaign QR Code",
    organizationLogoAlt: "Organization logo",
  },
  fr: {
    printButton: "Imprimer ou enregistrer en PDF",
    qrAlt: "Code QR de la campagne",
    organizationLogoAlt: "Logo de l’organisation",
  },
};

const sanitizeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getOrganizationLogoUrl = (logo) => {
  if (!logo) return "";
  if (/^https?:\/\//i.test(logo)) return logo;

  const s3BaseUrl = process.env.REACT_APP_S3_URL || "";
  if (!s3BaseUrl) return logo;

  return `${s3BaseUrl.replace(/\/+$/, "")}/${String(logo).replace(/^\/+/, "")}`;
};

const ViewCampaign = () => {
  const echartRef = useRef(null);
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const location = useLocation();
  const { t, i18n } = useCustomTranslation("View Campaign");
  const [selectedPeriod, setSelectedPeriod] = useState("today");
  const [pageFilter, setpageFilter] = useState({
    emplacement: "",
    campagne: "",
    kiosk: "",
    donation_source: "",
    start_date: dayjs(new Date()),
    end_date: dayjs(new Date()),
    start_time: dayjs().startOf("day").format("HH:mm"),
    end_time: dayjs().endOf("day").format("HH:mm"),
  });

  // Extract campaign_id from location.state
  const campaignId = location.state?.campaign_id;
  const userId =
    authContext.authContext?.accessToken?.user_id ||
    authContext.authContext?.accessToken?.id;
  const organizationId = authContext.authContext?.accessToken?.organization_id;

  // Build datetime strings for API queries
  const startDateTime = formatDateTime(pageFilter.start_date, pageFilter.start_time);
  const endDateTime = formatDateTime(pageFilter.end_date, pageFilter.end_time);

  // Query key for cache invalidation
  const filterKey = [
    campaignId,
    pageFilter.emplacement,
    pageFilter.kiosk,
    pageFilter.donation_source,
    startDateTime,
    endDateTime,
  ];

  // Only fetch when campaignId is available
  const hasCampaignFiltersReady = Boolean(campaignId);

  // Chart + tables + dialogs
  const [chartData, setChartData] = useState({});
  const [transactionData, setTransactionData] = useState([]);
  const [cashTableData, setCashTableData] = useState([]);
  const [selectedBorneData, setSelectedBorneData] = useState({});
  const [addCashDialogOpen, setAddCashDialogOpen] = useState(false);
  const [bornesInfoDialogOpen, setBornesInfoDialogOpen] = useState(false);

  // Campaign QR print dialog
  const [qrPrintDialogOpen, setQrPrintDialogOpen] = useState(false);
  const organizationLogoRequestRef = useRef(null);

  // Add cash inputs
  const [cashAmountInput, setCashAmountInput] = useState("");
  const [cashNotesInput, setCashNotesInput] = useState("");
  const [donationDate, setDonationDate] = useState("");

  // Alerts
  const { setShowSuccessAlert, setMessageSuccessAlert } = useSuccessAlert();

  // Totals
  const [totalCollected, setTotalCollected] = useState(0);

  /** -------------------------
   *  Queries
   *  ------------------------- */
  const locationsFilterQuery = useApiClientQuery(
    () => api_endpoints.getLocations(authContext.authContext.accessToken.organization_id),
    { queryKey: [`viewCampaign_locationFIlter_${authContext.authContext.accessToken.organization_id}`] },
    (data) => {
      if (data?.length === 1) changePageFilter("emplacement", data[0]._id);
    }
  );

  const kiosksFilterQuery = useApiClientQuery(
    () => api_endpoints.getKiosks(authContext.authContext.accessToken.organization_id, null, true),
    { queryKey: [`viewCampaign_kiosks_filter_${authContext.authContext.accessToken.organization_id}`] },
    (data) => {
      if (data?.length === 1) changePageFilter("kiosk", data[0]._id);
    }
  );

  const fetchCampaignQuery = useApiClientQuery(
    () =>
      api_endpoints.getCampaign(
        campaignId,
        pageFilter.emplacement,
        pageFilter.kiosk,
        startDateTime,
        endDateTime,
        pageFilter.donation_source
      ),
    {
      queryKey: ["viewCampaign_fetchCampaignData", ...filterKey],
      enabled: hasCampaignFiltersReady,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    (data) => {
      mapBorneData(data.bornes);
      mapCashDataTable(data.cashTransactions);
    }
  );

  const fetchCampaignTransactionsDataQuery = useApiClientQuery(
    () =>
      api_endpoints.getCampaignTransactions(
        campaignId,
        pageFilter.emplacement,
        pageFilter.kiosk,
        startDateTime,
        endDateTime,
        pageFilter.donation_source
      ),
    {
      queryKey: ["viewCampaign_fetchCampaignTransactionsData", ...filterKey],
      enabled: hasCampaignFiltersReady,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    (data) => {
      getTotalCollected(data);
      mapChartData(data);
    }
  );

  const fetchCampaignTransactionsStatsQuery = useApiClientQuery(
    () =>
      api_endpoints.getCampaignTransactionsStats(
        campaignId,
        pageFilter.emplacement,
        pageFilter.kiosk,
        startDateTime,
        endDateTime,
        pageFilter.donation_source
      ),
    {
      queryKey: ["fetchCampaignTransactionsStats_viewCampaign", ...filterKey],
      enabled: hasCampaignFiltersReady,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const addCampaignCashMutation = useApiClientMutation(
    api_endpoints.addCashToCampaign,
    ["fetchCampaignData_viewCampaign"],
    (data) => {
      setMessageSuccessAlert(data);
      setAddCashDialogOpen(false);
      setCashAmountInput("");
      setCashNotesInput("");
      setDonationDate("");
      setShowSuccessAlert(true);
      fetchCampaignQuery.refetch();
      fetchCampaignTransactionsDataQuery.refetch();
      fetchCampaignTransactionsStatsQuery.refetch();
    }
  );

  const archiveCampaignMutation = useApiClientMutation(
    api_endpoints.setArchiveCampaignStatus,
    ["fetchCampaignData_viewCampaign"],
    (data) => {
      setMessageSuccessAlert(data);
      setShowSuccessAlert(true);
      fetchCampaignQuery.refetch();
    }
  );

  /** -------------------------
   *  Handlers & helpers
   *  ------------------------- */
  const handlePeriodChange = (event) => setSelectedPeriod(event.target.value);
  const changePageFilter = (option, value) =>
    setpageFilter({
      ...pageFilter,
      [option]: value,
      ...(option === "donation_source" && value === "online" ? { kiosk: "" } : {}),
    });

  const clearpageFilter = () => {
    setSelectedPeriod("today");
    const updatedPageFilter = {
      emplacement: "",
      kiosk: "",
      donation_source: "",
      start_date: dayjs(new Date()),
      end_date: dayjs(new Date()),
      start_time: dayjs().startOf("day").format("HH:mm"),
      end_time: dayjs().endOf("day").format("HH:mm"),
    };
    if (locationsFilterQuery.data?.length === 1) updatedPageFilter.emplacement = locationsFilterQuery.data[0]._id;
    if (kiosksFilterQuery.data?.length === 1) updatedPageFilter.kiosk = kiosksFilterQuery.data[0]._id;
    setpageFilter(updatedPageFilter);
  };

  const setpersonalizedDates = (startDate, endDate, starttime, endtime) => {
    setpageFilter({
      ...pageFilter,
      start_date: startDate,
      end_date: endDate,
      start_time: starttime.format("HH:mm"),
      end_time: endtime.format("HH:mm"),
    });
  };

  // include card + cash + check + bank transfer in total
  const getTotalCollected = (transactions) => {
    let total = 0;
    const add = (arr) => {
      if (Array.isArray(arr)) for (const x of arr) total += toNumber(x.total);
    };
    add(transactions?.transactions); // card
    add(transactions?.cashDonations); // cash
    add(transactions?.checkDonations); // check
    add(transactions?.bankTransfers); // bank
    setTotalCollected(total);
  };

  // Build stacked bar chart data (only non-empty series)
  const mapChartData = (transactions) => {
    const card = transactions?.transactions ?? [];
    const cash = transactions?.cashDonations ?? [];
    const check = transactions?.checkDonations ?? [];
    const bank = transactions?.bankTransfers ?? [];
    const labelOf = (x) => x?.label ?? x?._id?.date; 

    const catSet = new Set([
      ...card.map(labelOf).filter(Boolean),
      ...cash.map(labelOf).filter(Boolean),
      ...check.map(labelOf).filter(Boolean),
      ...bank.map(labelOf).filter(Boolean),
    ]);

    // ISO-like labels sort chronologically: YYYY-MM or YYYY-MM-DD
    const categories = Array.from(catSet).sort();

    const toMap = (arr) =>
      new Map(arr.map((x) => [labelOf(x), { total: toNumber(x.total), count: toNumber(x.count) }]));

    const cardMap = toMap(card);
    const cashMap = toMap(cash);
    const checkMap = toMap(check);
    const bankMap = toMap(bank);

    const mkSeriesData = (map) =>
      categories.map((c) => {
        const v = map.get(c) || { total: 0, count: 0 };
        return { value: v.total, count: v.count, date: c };
      });

    const seriesCandidates = [
      { key: "card",  name: t("debitCredit") || "Debit/Credit", data: mkSeriesData(cardMap),  color: SERIES_PALETTE.card },
      { key: "cash",  name: t("cash")        || "Cash",         data: mkSeriesData(cashMap),  color: SERIES_PALETTE.cash },
      { key: "check", name: t("check")       || "Check",        data: mkSeriesData(checkMap), color: SERIES_PALETTE.check },
      { key: "bank",  name: t("bank_transfer") || "Bank Transfer", data: mkSeriesData(bankMap), color: SERIES_PALETTE.bank },
    ];

    const visibleSeries = seriesCandidates.filter((s) => s.data.some((d) => toNumber(d.value) > 0));

    const option = JSON.parse(JSON.stringify(viewCampaignChartOption));
    option.xAxis = { ...(option.xAxis || {}), type: "category", data: categories };
    option.yAxis = { ...(option.yAxis || {}), type: option.yAxis?.type || "value" };
    option.title = { ...(option.title || {}), text: `${t("collections_for")} ${fetchCampaignQuery.data?.campaign?.name ?? ""}` };
    option.color = visibleSeries.map((s) => s.color);
    option.series = visibleSeries.map((s) => ({ name: s.name, type: "bar", stack: "donations", data: s.data }));
    option.legend = { data: visibleSeries.map((s) => s.name) };
    option.tooltip = {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        let totalAmount = 0;
        let totalCount = 0;
        const rows = params.map((p) => {
          const val = typeof p.data === "object" && p.data?.value != null ? p.data.value : p.value;
          const cnt = p.data?.count ?? 0;
          totalAmount += toNumber(val);
          totalCount += toNumber(cnt);
          return `${p.marker} ${p.seriesName}: <b>${formatCurrency(val)} $</b> (${t("transactions") || "transactions"}: ${cnt})`;
        });
        const axis = params?.[0]?.axisValueLabel ?? "";
        return [
          `<div>${axis}</div>`,
          ...rows,
          `<div style="margin-top:6px"><b>${t("total") || "Total"}:</b> ${formatCurrency(totalAmount)} $ — ${t("all_donations_count") || "All donations count"}: ${totalCount}</div>`,
        ].join("<br/>");
      },
    };

    setChartData(option);
  };

  const mapBorneData = (bornes) => {
    const data = (bornes ?? []).map((borne) => ({
      id: borne._id,
      kiosk_name: borne.doc?.borne?.name,
      location: borne.doc?.borne?.location?.address,
      amount_collected: toNumber(borne.total),
      montant_recolte_percent: toNumber(borne.montant_recolte_percent),
      number_of_transactions: toNumber(borne.transactions),
      nombre_transaction_percent: toNumber(borne.nombre_transaction_percent),
    }));
    setTransactionData(data);
  };

  const mapCashDataTable = (tableData) => {
    const data = (tableData ?? []).map((transaction) => ({
      cashAmount: toNumber(transaction.transaction?.amount),
      date: dateTimeReadable(transaction.transaction?.created_at),
      date_donated: dateReadable(transaction.date_of_donation),
      inserted_by: `${transaction.user?.last_name ?? ""} ${transaction.user?.first_name ?? ""}`.trim(),
      notes: transaction.notes || "",
    }));
    setCashTableData(data);
  };

  const handleAddCashToCampaign = async (e) => {
    e.preventDefault();
    addCampaignCashMutation.mutate({
      campaign_id: campaignId,
      cash_amount: cashAmountInput,
      inserted_by: userId,
      organization_id: organizationId,
      date_donated: donationDate,
      notes: cashNotesInput.trim(),
    });
  };

  const handleArchiveCampaign = async () => {
    archiveCampaignMutation.mutate({
      campaign_id: fetchCampaignQuery.data.campaign._id,
      archive_status: !fetchCampaignQuery.data?.campaign?.is_archived,
    });
  };

  const handleDeleteCampaign = async () => {
    const response = await api_endpoints.deleteCampaign(campaignId);
    if (response.status === 200) {
      setMessageSuccessAlert(response.data);
      setShowSuccessAlert(true);
      navigate(getPageFromName("Campagnes").path);
    }
  };

  const selectBorne = (e) => {
    if (!fetchCampaignQuery.data?.bornes) return;
    for (let borne of fetchCampaignQuery.data.bornes) {
      if (borne._id.borne === e.id.borne) {
        setSelectedBorneData(borne);
        setBornesInfoDialogOpen(true);
        break;
      }
    }
  };

  const getCampaignNameForLanguage = (language) => {
    const campaign = fetchCampaignQuery.data?.campaign;
    const translations = campaign?.campaign_locale || [];
    const translatedCampaign = translations.find((translation) => {
      const abbreviation = String(translation?.locale?.locale_abbreviation || "")
        .toLowerCase()
        .replace("-", "_");
      return abbreviation === language || abbreviation.startsWith(`${language}_`);
    });

    return translatedCampaign?.campaign_name || campaign?.name || "";
  };

  const loadOrganizationLogo = useCallback(() => {
    if (organizationLogoRequestRef.current) {
      return organizationLogoRequestRef.current;
    }

    const request = api_endpoints
      .getOrganization(organizationId)
      .then((organizationResponse) => {
        const logo =
          organizationResponse?.data?.logo || organizationResponse?.data?.logo_url || "";
        return getOrganizationLogoUrl(logo);
      })
      .catch((organizationError) => {
        console.error("Error loading organization logo", organizationError);
        return "";
      })
      .finally(() => {
        if (organizationLogoRequestRef.current === request) {
          organizationLogoRequestRef.current = null;
        }
      });

    organizationLogoRequestRef.current = request;
    return request;
  }, [organizationId]);

  const handleOpenQrPrintDialog = () => {
    setQrPrintDialogOpen(true);
  };

  const handleGenerateQRCode = async ({
    language,
    message,
    campaignName,
    logoUrl,
  }) => {
    const fundraiserToken = fetchCampaignQuery.data?.campaign?.fundraiser_url;
    if (!fundraiserToken) {
      console.error("Fundraiser URL not available for this campaign");
      return;
    }

    const qrValue = fundraiserToken.startsWith("http")
      ? fundraiserToken
      : `${BASE_URL}/donor-fundraiser/${fundraiserToken}`;

    const printWindow = window.open("", "", "width=1000,height=1000");
    if (!printWindow) {
      console.error("Print preview window was blocked");
      return;
    }

    try {
      const qrImage = await QRCode.toDataURL(qrValue, { width: 360 });
      const resolvedLogoUrl = logoUrl || (await loadOrganizationLogo());

      openPrintPreview({
        printWindow,
        message,
        campaignName,
        qrImage,
        qrSize: 360,
        logoUrl: resolvedLogoUrl,
        language,
      });
      setQrPrintDialogOpen(false);
    } catch (err) {
      printWindow.close();
      console.error("Error generating QR Code", err);
    }
  };

  const openPrintPreview = ({
    printWindow,
    message,
    campaignName,
    qrImage,
    qrSize,
    logoUrl,
    language,
  }) => {
    const copy = PRINT_COPY[language] || PRINT_COPY.en;
    const safeMessage = sanitizeHtml(message);
    const safeCampaignName = sanitizeHtml(campaignName);
    const safeQrImage = sanitizeHtml(qrImage);
    const safeLogoUrl = sanitizeHtml(logoUrl);

    printWindow.document.open();
    printWindow.document.write(`
      <html lang="${language}">
        <head>
          <meta charset="utf-8" />
          <title>${safeCampaignName || sanitizeHtml(copy.qrAlt)}</title>
          <style>
            @page { margin: 0; size: auto; }
            body { font-family: Arial, sans-serif; text-align: center; padding: 0; margin: 0; display: flex; flex-direction: column; min-height: 100vh; box-sizing: border-box; }
            .content { display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center; flex: 1 0 auto; padding: 40px; box-sizing: border-box; }
            .organization-logo { max-width: 150px; max-height: 110px; object-fit: contain; margin-bottom: 24px; }
            h2 { margin: 0 0 20px; font-size: 24px; white-space: pre-wrap; max-width: 720px; overflow-wrap: anywhere; }
            h1 { margin: 0 0 20px; font-size: 36px; max-width: 720px; overflow-wrap: anywhere; }
            .qr-code-container { margin-top: 20px; margin-bottom: 40px; }
            .footer { width: 100%; text-align: center; padding: 10px 0 20px; background-color: white; box-sizing: border-box; }
            .no-print { margin-bottom: 30px; }
            @media print {
              body { margin: 0; padding: 0; min-height: 100vh; }
              .content { padding: 30px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="content">
            <div class="no-print"><button onclick="window.print()">${sanitizeHtml(
              copy.printButton
            )}</button></div>
            ${
              safeLogoUrl
                ? `<img src="${safeLogoUrl}" alt="${sanitizeHtml(
                    copy.organizationLogoAlt
                  )}" class="organization-logo" />`
                : ""
            }
            ${safeMessage ? `<h2>${safeMessage}</h2>` : ""}
            ${safeCampaignName ? `<h1>${safeCampaignName}</h1>` : ""}
            <div class="qr-code-container">
              <img src="${safeQrImage}" alt="${sanitizeHtml(
                copy.qrAlt
              )}" width="${qrSize}" height="${qrSize}" />
            </div>
          </div>
          <div class="footer">
            <img src="${process.env.PUBLIC_URL}/Kiosk-Donations.svg" alt="Kiosk Donations" width="150" />
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  /** Effects */
  useEffect(() => {
    const onResize = () => echartRef?.current?.getEchartsInstance().resize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /** Derived totals from STATS (include all types) */
  const stats = fetchCampaignTransactionsStatsQuery.data;
  const totalsFromStats = useMemo(() => {
    const cardTotal = toNumber(stats?.transactionsStats?.total);
    const cashTotal = toNumber(stats?.cashDonationsStats?.total);
    const checkTotal = toNumber(stats?.checkDonationsStats?.total);
    const bankTotal = toNumber(stats?.bankTransfersStats?.total);

    const cardCount = toNumber(stats?.transactionsStats?.count);
    const cashCount = toNumber(stats?.cashDonationsStats?.count);
    const checkCount = toNumber(stats?.checkDonationsStats?.count);
    const bankCount = toNumber(stats?.bankTransfersStats?.count);

    return {
      amount: cardTotal + cashTotal + checkTotal + bankTotal,
      count: cardCount + cashCount + checkCount + bankCount,
      cashAvg: toNumber(stats?.cashDonationsStats?.avg),
      cashMax: toNumber(stats?.cashDonationsStats?.max),
      cardAvg: toNumber(stats?.transactionsStats?.avg),
      cardMax: toNumber(stats?.transactionsStats?.max),
      checkAvg: toNumber(stats?.checkDonationsStats?.avg),
      checkMax: toNumber(stats?.checkDonationsStats?.max),
      bankAvg: toNumber(stats?.bankTransfersStats?.avg),
      bankMax: toNumber(stats?.bankTransfersStats?.max),
    };
  }, [stats]);

  const safeTotalAmount = totalsFromStats.amount > 0 ? totalsFromStats.amount : totalCollected;

  // Visibility booleans for stats panel
  const hasCard = toNumber(stats?.transactionsStats?.count) > 0;
  const hasCash = toNumber(stats?.cashDonationsStats?.count) > 0;
  const hasCheck = toNumber(stats?.checkDonationsStats?.count) > 0;
  const hasBank = toNumber(stats?.bankTransfersStats?.count) > 0;

  const isLoading =
    (fetchCampaignQuery.isLoading && !fetchCampaignQuery.data) ||
    (fetchCampaignTransactionsDataQuery.isLoading && !fetchCampaignTransactionsDataQuery.data) ||
    (fetchCampaignTransactionsStatsQuery.isLoading && !fetchCampaignTransactionsStatsQuery.data);

  const isError =
    fetchCampaignQuery.isError ||
    fetchCampaignTransactionsDataQuery.isError ||
    fetchCampaignTransactionsStatsQuery.isError;

  // Guard: redirect or show error if campaignId is missing
  if (!campaignId) {
    return (
      <div className="container-white mt-8 p-8 text-center">
        <h2 className="text-xl font-semibold text-red-600 mb-4">
          {t("error") || "Error"}
        </h2>
        <p className="mb-4">
          {t("campaign_id_missing") || "Campaign ID is missing. Please select a campaign from the campaigns list."}
        </p>
        <button
          className="btn-primary"
          onClick={() => navigate(getPageFromName("Campagnes").path)}
        >
          {t("go_to_campaigns") || "Go to Campaigns"}
        </button>
      </div>
    );
  }

  return (
    <LoadingContainer loading={isLoading} error={isError}>
      {/* Dialogs */}
      <AddCashDialog
        open={addCashDialogOpen}
        onClose={() => setAddCashDialogOpen(false)}
        onSubmit={handleAddCashToCampaign}
        cashAmountInput={cashAmountInput}
        setCashAmountInput={setCashAmountInput}
        cashNotesInput={cashNotesInput}
        setCashNotesInput={setCashNotesInput}
        donationDate={donationDate}
        setDonationDate={setDonationDate}
        t={t}
      />

      <KioskInfoDialog
        open={bornesInfoDialogOpen}
        onClose={() => setBornesInfoDialogOpen(false)}
        selectedBorneData={selectedBorneData}
        t={t}
      />

      {/* Filters */}
      <FiltersBar
        t={t}
        pageFilter={pageFilter}
        locations={locationsFilterQuery.data}
        kiosks={kiosksFilterQuery.data}
        selectedPeriod={selectedPeriod}
        handlePeriodChange={handlePeriodChange}
        setSelectedPeriod={setSelectedPeriod}
        setpersonalizedDates={setpersonalizedDates}
        changePageFilter={changePageFilter}
        clearpageFilter={clearpageFilter}
      />

      {/* Chart + Totals */}
      <div className={`mt-6 flex flex-1 space-x-4 ${styles.graph_container}`}>
        <ChartAndTotals
          echartRef={echartRef}
          chartData={chartData}
          totalsCount={totalsFromStats.count}
          safeTotalAmount={safeTotalAmount}
          t={t}
        />
      </div>

      {/* Details */}
      <Box className="container-white mt-8 w-full" sx={{ width: "100%", maxWidth: "100%", mx: 0 }}>
        <Box className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
          <Box className="min-w-0 w-full">
            <CampaignDetails
              campaign={fetchCampaignQuery.data?.campaign}
              t={t}
              safeTotalAmount={safeTotalAmount}
              formatCurrency={formatCurrency}
            />
          </Box>
          <Box className="min-w-0 w-full">
            <StatsPanel
              t={t}
              stats={stats}
              totalsFromStats={totalsFromStats}
              hasCard={hasCard}
              hasCash={hasCash}
              hasCheck={hasCheck}
              hasBank={hasBank}
            />
          </Box>
        </Box>
      </Box>

      {/* Buttons */}
      <ButtonsBar
        styles={styles}
        t={t}
        campaign={fetchCampaignQuery.data?.campaign}
        onPrintQR={handleOpenQrPrintDialog}
        onOpenAddCash={() => setAddCashDialogOpen(true)}
        onEditCampaign={() => {
          if (!campaignId) return;
          navigate(getPageFromName("Modifier la campagne").path, { state: { campaign_id: campaignId } });
        }}
        onDelete={handleDeleteCampaign}
        onArchive={handleArchiveCampaign}
      />

      <CampaignQrPrintDialog
        open={qrPrintDialogOpen}
        t={t}
        uiLanguage={i18n.language}
        campaignNameEn={getCampaignNameForLanguage("en")}
        campaignNameFr={getCampaignNameForLanguage("fr")}
        loadLogo={loadOrganizationLogo}
        onCancel={() => setQrPrintDialogOpen(false)}
        onGenerate={handleGenerateQRCode}
      />

      {/* Tables */}
      <div className="flex flex-1 flex-col space-y-4">
        <KiosksTable t={t} data={transactionData} onRowClicked={selectBorne} />
        <CashDonationsTable t={t} data={cashTableData} />
      </div>
    </LoadingContainer>
  );
};

export default ViewCampaign;
